<template>
  <div
    id="svideo"
    class="video-popup-section"
  >
    <div class="container">
      <h2 v-html="stripBlockTags(section.header)" />
      <a
        v-if="section.button_text"
        :class="`image-container dir-${section.img_side}`"
        @click="showEmbedModal(section.video_embed)"
        @keypress="showEmbedModal(section.video_embed)"
      >
        <div
          class="aspect"
          :class="`ratio-${section.aspect_ratio}`"
        >
          <img
            loading="lazy"
            :src="section.video_thumbnail.url"
            :alt="section.video_thumbnail.alt"
          >
        </div>
      </a>
      <div
        class="content"
        v-html="section.content"
      />
      <div v-if="section.button_text">
        <a
          class="link btn"
          :class="btnClass"
          @click="showEmbedModal(section.video_embed)"
          @keypress="showEmbedModal(section.video_embed)"
        >
          {{ section.button_text }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import RysEmbedModal from '@/components/modals/EmbedModal.vue';
import stripBlockTags from '@/utils/strip-block-tags';

export default {
  name: 'SplitImageSection',

  props: {
    section: { type: Object, required: true }
  },

  setup() {
    const modal = useModal();
    return { modal };
  },

  data() {
    return {
      screenWidth: 0
    };
  },

  computed: {
    btnClass() {
      if (this.section.button_text) {
        if (this.section.style === 'fancy') {
          return 'black';
        }

        if (this.section.bg_color === 'white') {
          return 'ghost';
        }
      }

      return '';
    },

    imgXPos() {
      if (this.screenWidth >= 1170) {
        return this.section.img_x_pos;
      }
      return this.section.mob_img_x_pos;
    },

    imgYPos() {
      if (this.screenWidth >= 1170) {
        return this.section.img_y_pos;
      }
      return this.section.mob_img_y_pos;
    }
  },

  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    this.onWindowResize();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },

  methods: {
    stripBlockTags,

    showEmbedModal(html) {
      this.modal(RysEmbedModal, this, { props: { embed: html } });
    },

    onLinkClick(e, section) {
      if (section.link_shows_video && section.video) {
        e.preventDefault();
        this.showEmbedModal(section.video);
      }
    },

    onWindowResize() {
      this.screenWidth = window.innerWidth;
    }
  }
};
</script>

<style lang="scss" scoped>
@use 'sass:math';

div.video-popup-section {
  position: relative;
  overflow-x: clip;

  .bg {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;

    .orange {
      position: relative;
      background-color: $thd-orange;
      height: 100%;
      width: 100%;
      transform: rotateZ(2deg) scale(110%);
      z-index: 2;

      @media screen and (min-width: $breakpoint-lg) {
        width: 50%;
        transform: rotateZ(2deg) scale(105%);
      }
    }

    .img {
      height: 100%;
      padding-top: 0;
      position: absolute;
      right: 0;
      width: 50%;
      z-index: 1;

      img {
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
    }
  }

  .container {
    position: relative;
    z-index: 2;
    display: grid;
    gap: 24px;
    grid-template-columns: minmax(auto, 100%);
    grid-template-rows: repeat(4, minmax(0, auto));

    @media screen and (min-width: $breakpoint-lg) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: repeat(2, auto) minmax(0, 1fr);
      grid-auto-flow: column dense;
      gap: 24px 60px;
    }

    .image-container,
    .video-container {
      @media screen and (min-width: $breakpoint-lg) {
        order: 9999;
        grid-row: span 3 / span 3;

        &.dir-left {
          order: -9999;
        }
      }

      .aspect {
        position: relative;
        width: 100%;
        padding-top: math.div(9, 16) * 100%;

        @media screen and (min-width: $breakpoint-lg) {
          padding-top: 100%;
        }

        &.ratio-landscape {
          padding-top: math.div(9, 16) * 100%;
        }

        > img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border: 2px solid;
          cursor: pointer;

          &.clickable {
            cursor: pointer;
          }
        }
      }

      .embed::v-deep {
        width: 100%;
        height: 0;
        overflow: hidden;
        position: relative;
        padding-top: math.div(9, 16) * 100%;

        iframe {
          width: 100% !important;
          height: 100% !important;
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }
  }

  &.bg-white {
    background-color: $thd-white;

    h2 {
      color: $thd-black;
    }

    .content {
      color: $thd-dark-gray;
    }
  }

  &.bg-black {
    background-color: $thd-black;

    h2 {
      color: $thd-white;
    }

    .content {
      color: $thd-white;
    }
  }

  &.style-fancy {
    margin-top: 64px;

    @media screen and (min-width: $breakpoint-lg) {
      margin-top: 0;
    }

    .bg {
      display: flex;

      &.dir-left {
        flex-direction: row-reverse;

        .orange {
          transform: rotateZ(-2deg) scale(110%);

          @media screen and (min-width: $breakpoint-lg) {
            transform: rotateZ(-2deg) scale(105%);
          }
        }

        .img {
          left: 0;
          right: unset;
        }
      }
    }

    .container::v-deep {
      padding-top: 32px;

      @media screen and (min-width: $breakpoint-lg) {
        padding-top: 60px;
      }

      h2 {
        color: $thd-white;
      }

      .content {
        color: $thd-white;

        strong,
        b {
          color: $thd-black;
        }
      }

      .image-container {
        @media screen and (min-width: $breakpoint-lg) {
          visibility: hidden;
        }
      }
    }
  }
}
</style>
